import { useEffect, useRef } from "react";
import { useAssistantBinding } from "../../react/providers/AssistantBindingProvider";

// Nav guard — when the user navigates to a different action while a turn
// is mid-stream, the panel's binding key changes. We can't block the
// nav (TanStack Router has already advanced), but we CAN warn the user
// and abort the in-flight turn so its deltas don't keep landing in a
// stale bubble.
//
// Mounts inside AssistantPanel. Watches the binding's anchored_action_
// key — when it changes mid-stream, fires onAbortAndClear so the
// panel cancels the AbortController + closes the old session.

interface Props {
    /** True while an SSE turn is in flight. */
    isStreaming: boolean;
    /** Fired when the user navigates during streaming — panel should
        abort the request + clear the visible "..." indicator. */
    onAbortAndClear: () => void;
}

export function AssistantNavigationGuard({ isStreaming, onAbortAndClear }: Props) {
    const binding = useAssistantBinding();
    const lastKeyRef = useRef<string | null>(binding.anchoredActionKey);

    useEffect(() => {
        if (!isStreaming) {
            lastKeyRef.current = binding.anchoredActionKey;
            return;
        }
        if (lastKeyRef.current !== binding.anchoredActionKey) {
            // Navigated mid-stream — abort.
            lastKeyRef.current = binding.anchoredActionKey;
            onAbortAndClear();
        }
    }, [binding.anchoredActionKey, isStreaming, onAbortAndClear]);

    // beforeunload — warn the user if they're about to close the tab
    // with a turn in flight (TanStack Router doesn't fire on this).
    useEffect(() => {
        if (!isStreaming) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isStreaming]);

    return null;
}
